import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Role, User } from '../../Types';
import { COLORS } from '../../Styles/Colors';
import useGetData from '../../Hooks/useGetData';
import apiClient from '../../Services/ApiClient';
import { useSnackbar } from '../../Context/SnackbarProvider';
import { MultiSelect } from '../../Components/Checkbox/Checkbox';
import { decode } from '../../Services/DecodeToken';
import useGetCustomData from '../../Hooks/useGetCustomData';
import LocationChecklist from '../../Components/Checkbox/LocationCheckList';
import EmailForm from './EmailForm';
import {
  FormShell,
  FormBody,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormSelect,
  FormActions,
  PhoneNumberField,
} from '../../Components/Form';

const TabHeader = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  padding: 20px 32px 0;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  background: ${({ $active }) => ($active ? COLORS.teal : COLORS.white)};
  color: ${({ $active }) => ($active ? COLORS.white : COLORS.teal)};
  border: none;
  border-bottom: ${({ $active }) => ($active ? `2px solid ${COLORS.darkTeal}` : 'none')};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 6px 6px 0 0;
`;

export type UserFormInput = Omit<User, 'ID' | 'password_reset_url' | 'image' | 'post_order_for_worker_flag'>;

const UserForm: React.FC<{
  onSubmit: (data: UserFormInput) => void;
  onCancel: () => void;
  defaultValues?: User;
}> = ({ onSubmit, onCancel, defaultValues }) => {
  const [activeTab, setActiveTab] = useState<'details' | 'subscriptions'>('details');

  const [formData, setFormData] = useState<any>({
    RFID: '',
    username: '',
    first_name: '',
    last_name: '',
    contact_number: '',
    user_type: undefined,
    password: '',
    email: '',
    IsActive: false,
    IsConsumed: false,
    FullControl: false,
    IsEditable: true,
    role_id: undefined,
    OVID: [] as number[],
  });

  const [roles, loadingRoles] = useGetData<Role>('/api/roles');
  const token: any = decode();
  const [ovs, loadingOvs] = useGetCustomData('/api/location/user/ovs');
  const [ovsUser, loadingOvsUser] = useGetCustomData(
    defaultValues?.ID ? `/api/location/user/ovs/${defaultValues.ID}` : ''
  );

  const mergedOvs = useMemo(() => {
    const map = new Map<number, any>();
    if (Array.isArray(ovs)) {
      for (const loc of ovs) {
        if (loc && typeof loc.OVID === 'number') map.set(loc.OVID, loc);
      }
    }
    if (Array.isArray(ovsUser)) {
      for (const loc of ovsUser) {
        if (loc && typeof loc.OVID === 'number' && !map.has(loc.OVID)) {
          map.set(loc.OVID, loc);
        }
      }
    }
    return Array.from(map.values());
  }, [ovs, ovsUser]);

  const selectedOvids = useMemo(() => {
    if (!Array.isArray(ovsUser)) return [];
    return ovsUser.map((loc) => loc.OVID).filter((ovid) => typeof ovid === 'number');
  }, [ovsUser]);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({ username: false, confirmPassword: false });
  const [usernameError, setUsernameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  // Remount phone field when switching create ↔ edit user
  const phoneFieldKey = defaultValues?.ID != null ? `edit-${defaultValues.ID}` : 'create';

  useSnackbar();

  const permissionOptions = [
    { id: 'IsConsumed', name: 'Is Consumed' },
    { id: 'FullControl', name: 'Full Control' },
    { id: 'IsEditable', name: 'Is Editable' },
  ];

  const selectedPermissions = permissionOptions
    .filter((opt) => formData[opt.id as keyof Pick<UserFormInput, 'IsConsumed' | 'FullControl' | 'IsEditable'>])
    .map((opt) => opt.id);

  const handleOvidChecklistChange = (e: { target: { name: string; value: number[] } }) => {
    const values = Array.isArray(e.target.value) ? e.target.value : [];
    setFormData((prev: any) => ({ ...prev, OVID: values }));
  };

  const handlePermissionChange = (selected: string[]) => {
    const updatedPermissions = permissionOptions.reduce((acc, perm) => {
      acc[perm.id] = selected.includes(perm.id);
      return acc;
    }, {} as Record<string, boolean>);
    setFormData((prev: any) => ({ ...prev, ...updatedPermissions }));
  };

  useEffect(() => {
    if (defaultValues) {
      setFormData({
        RFID: defaultValues.RFID || '',
        username: defaultValues.username || '',
        first_name: defaultValues.first_name || '',
        last_name: defaultValues.last_name || '',
        contact_number: defaultValues.contact_number || '',
        user_type: defaultValues.user_type || 0,
        password: '',
        email: defaultValues.email || '',
        IsActive: !!defaultValues.IsActive,
        IsConsumed: !!defaultValues.IsConsumed,
        FullControl: !!defaultValues.FullControl,
        IsEditable: !!defaultValues.IsEditable,
        role_id: defaultValues.role_id,
        OVID: [],
      });
    }
  }, [defaultValues]);

  useEffect(() => {
    if (defaultValues && selectedOvids.length > 0) {
      setFormData((prev: any) => ({ ...prev, OVID: selectedOvids }));
    }
  }, [selectedOvids, defaultValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (phoneError) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkUsernameExists = async (username: string): Promise<boolean> => {
    try {
      const res = await apiClient('/api/users/check-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });
      const data = await res.json();
      return data.exists;
    } catch {
      return false;
    }
  };

  const isConfirmPasswordError =
    touched.confirmPassword && confirmPassword !== formData.password;

  const roleOptions = roles
    .filter((role) => !(role.id === 1 && token.roles.id !== 1))
    .map((role) => ({ value: role.id, label: role.name }));

  return (
    <FormShell $embedded>
      <TabHeader>
        <TabButton $active={activeTab === 'details'} type="button" onClick={() => setActiveTab('details')}>
          User Details
        </TabButton>
        {defaultValues && (
          <TabButton $active={activeTab === 'subscriptions'} type="button" onClick={() => setActiveTab('subscriptions')}>
            Email Subscriptions
          </TabButton>
        )}
      </TabHeader>

      {activeTab === 'details' && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <FormBody>
            <FormSection>
              <SectionTitle>Account Information</SectionTitle>
              <SectionDescription>Basic user credentials and contact details.</SectionDescription>
              <FormGrid>
                <FormTextField label="RFID" name="RFID" value={formData.RFID} onChange={handleChange} />
                <FormTextField
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={async () => {
                    setTouched((prev) => ({ ...prev, username: true }));
                    if (formData.username.trim() !== '') {
                      const exists = await checkUsernameExists(formData.username);
                      setUsernameError(exists ? 'Username is already taken' : '');
                    }
                  }}
                  error={Boolean(usernameError) && touched.username}
                  helperText={touched.username ? usernameError : ''}
                />
                <FormTextField
                  label="User Type"
                  name="user_type"
                  type="number"
                  value={formData.user_type || ''}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                  onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                />
                <FormTextField label="First Name" name="first_name" required value={formData.first_name} onChange={handleChange} />
                <FormTextField label="Last Name" name="last_name" required value={formData.last_name} onChange={handleChange} />
                <PhoneNumberField
                  key={phoneFieldKey}
                  value={formData.contact_number || ''}
                  onChange={(e164) =>
                    setFormData((prev: any) => ({ ...prev, contact_number: e164 }))
                  }
                  onValidationChange={setPhoneError}
                />
                <FormTextField label="Email" type="email" required name="email" value={formData.email} onChange={handleChange} />
              </FormGrid>
            </FormSection>

            {!defaultValues && (
              <FormSection>
                <SectionTitle>Password</SectionTitle>
                <FormGrid $columns={1}>
                  <FormTextField label="Password" required type="password" name="password" value={formData.password} onChange={handleChange} />
                  <FormTextField
                    label="Confirm Password"
                    type="password"
                    required
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, confirmPassword: true }))}
                    error={isConfirmPasswordError}
                    helperText={isConfirmPasswordError ? 'Passwords do not match' : ''}
                  />
                </FormGrid>
              </FormSection>
            )}

            <FormSection>
              <SectionTitle>Role &amp; Locations</SectionTitle>
              <FormGrid $columns={1}>
                <FormSelect
                  id="role_id"
                  label="Role"
                  name="role_id"
                  value={formData.role_id ?? ''}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, role_id: e.target.value ? Number(e.target.value) : undefined }))}
                  disabled={loadingRoles}
                  placeholder="Select Role"
                  options={roleOptions}
                />
                <LocationChecklist
                  name="OVID"
                  selectedValues={formData.OVID}
                  onChange={handleOvidChecklistChange}
                  ovs={mergedOvs}
                  loadingOvs={loadingOvs || loadingOvsUser}
                  disabled={loadingOvs || loadingOvsUser}
                />
              </FormGrid>
            </FormSection>

            {/* Permissions section temporarily disabled
            <FormSection>
              <SectionTitle>Permissions</SectionTitle>
              <MultiSelect
                title="Permissions"
                items={permissionOptions}
                selectedItems={selectedPermissions}
                setSelectedItems={handlePermissionChange}
                itemKey="id"
                showBorder={false}
              />
            </FormSection>
            */}
          </FormBody>

          <FormActions
            submitLabel="Save User"
            onCancel={onCancel}
            isLoading={isSubmitting}
            disabled={!formData.OVID || formData.OVID.length === 0 || Boolean(phoneError)}
          />
        </form>
      )}

      {activeTab === 'subscriptions' && defaultValues && (
        <EmailForm userId={defaultValues.ID} onCancel={onCancel} />
      )}
    </FormShell>
  );
};

export default UserForm;
